import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { languageFromString } from 'dg-web-shared/lib/Text.ts';
import { Spinner } from '../ui/loaders/Spinner.tsx';
import { ConnectedChangePasswordFormController } from './ChangePasswordFormController.tsx';
import { useSearchParams, useNavigate, Navigate } from 'react-router';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useEffect } from 'react';
import {
    ConfirmationDialogVersions,
    ConfirmationDialog,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { enforcementUrl } from '../utils/UrlUtil.ts';

export function ConnectedChangePasswordController() {
    const [searchParams] = useSearchParams();
    const { language, setLanguage } = useLanguage();

    const languageFromParam = languageFromString(
        searchParams.get('language') ?? 'de',
    );
    const sourceParam = searchParams.get('source');
    const source = isPasswordChangeTokenSource(sourceParam)
        ? sourceParam
        : PasswordChangeTokenSource.APP;

    const token = searchParams.get('token') ?? '';

    useEffect(() => {
        if (language !== languageFromParam) {
            setLanguage(languageFromParam);
        }
    }, [language, languageFromParam, setLanguage]);

    if (token.length === 0) {
        return <Navigate to="/" replace />;
    }

    return <ChangePasswordController source={source} token={token} />;
}

export enum PasswordChangeTokenSource {
    APP = 'APP',
    WEB = 'WEB',
    MOBILE = 'MOBILE',
}

function isPasswordChangeTokenSource(
    value: string | null,
): value is PasswordChangeTokenSource {
    return Object.values(PasswordChangeTokenSource).includes(
        value as PasswordChangeTokenSource,
    );
}

export function useNavigateToSourceLogin(source: PasswordChangeTokenSource) {
    const navigate = useNavigate();
    return () => {
        switch (source) {
            case PasswordChangeTokenSource.APP:
                window.location.href = enforcementUrl();
                break;
            case PasswordChangeTokenSource.WEB:
                navigate('/');
                break;
            case PasswordChangeTokenSource.MOBILE:
                // full page navigation for the native app
                window.location.href = '/mobile/login';
                break;
        }
    };
}

export function SourceLoginConfirmLabel({
    source,
}: {
    source: PasswordChangeTokenSource;
}) {
    return source === PasswordChangeTokenSource.WEB ? (
        <Localized de="Schliessen" fr="Fermer" it="Chiudi" en="Close" />
    ) : (
        <Localized
            de="Zum App-Login"
            fr="Accéder à la connexion"
            it="Torna al login"
            en="Go to app login"
        />
    );
}

function ChangePasswordController({
    token,
    source,
}: {
    token: string;
    source: PasswordChangeTokenSource;
}) {
    const navigateToSourceLogin = useNavigateToSourceLogin(source);

    const [userIdentityState] = useServerFetch<
        GetUserIdentityResponse,
        { token: string },
        object
    >(
        ({ token }) => ({
            url:
                '/ui-api/operator-account/password-change-token/' +
                encodeURIComponent(token),
        }),
        { token },
    );

    switch (userIdentityState.status) {
        case RequestStatus.NEVER_EXECUTED:
            return <Spinner loading={true} />;

        case RequestStatus.PENDING:
            return <Spinner loading={true} />;

        case RequestStatus.ERROR:
            return (
                <ConfirmationDialog
                    title={
                        <Localized
                            de="Link ungültig"
                            fr="Lien non valide"
                            it="Link non valido"
                            en="Link is invalid"
                        />
                    }
                    open={true}
                    dialogVersion={ConfirmationDialogVersions.ERROR}
                    confirmLabel={<SourceLoginConfirmLabel source={source} />}
                    onConfirm={navigateToSourceLogin}
                    closeDisabled={true}
                >
                    <>
                        <p>
                            <Localized
                                de="Der Link ist fehlerhaft, wurde bereits verwendet oder ist abgelaufen."
                                fr="Le lien est incorrect, il a déjà été utilisé ou il a expiré."
                                it="Il link è errato, è stato già utilizzato oppure è scaduto."
                                en="The link is wrong, has already been used or has expired."
                            />
                        </p>
                        <p>
                            <Localized
                                de="Fordern Sie einen neuen Link an und versuchen Sie es erneut."
                                fr="Veuillez demander un nouveau lien et réessayer."
                                it="Richiedere un nuovo link e riprovare."
                                en="Request a new link and try again."
                            />
                        </p>
                    </>
                </ConfirmationDialog>
            );

        case RequestStatus.SUCCESS:
            return (
                <ConnectedChangePasswordFormController
                    token={token}
                    source={source}
                />
            );
    }
}

interface GetUserIdentityResponse {
    operatorLogin: { firstName: string; lastName: string };
}
